import { useState, useRef, useEffect } from 'react';
import Button from '../components/Button';
import { submitSupportRequest } from '../services/supportService';

export default function Support() {
  const [problem, setProblem] = useState('');
  const [details, setDetails] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const imageRef = useRef(null);

  useEffect(() => {
    if (imageRef.current?.complete) {
      setImageLoaded(true);
    }
  }, []);

  function validate() {
    const errors = {};
    if (!problem.trim()) {
      errors.problem = 'Please describe the problem you are having.';
    }
    if (!details.trim()) {
      errors.details = 'Please provide more details.';
    }
    if (!name.trim()) {
      errors.name = 'Name is required.';
    }
    if (!email.trim()) {
      errors.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Please enter a valid email address.';
    }
    return errors;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    setIsLoading(true);

    try {
      await submitSupportRequest({
        name: name.trim(),
        email: email.trim(),
        problem: problem.trim(),
        details: details.trim(),
      });
      setIsSubmitted(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="support-page">
      <div className={`support-left${imageLoaded ? ' is-loaded' : ''}`}>
        {!imageLoaded && <div className="support-left-shimmer" aria-hidden="true" />}
        <img
          ref={imageRef}
          src={`${process.env.PUBLIC_URL}/images/login-left.png`}
          alt=""
          onLoad={() => setImageLoaded(true)}
        />
      </div>

      <form className="support-form" onSubmit={handleSubmit}>
        <div className="support-logo">
          <img src={`${process.env.PUBLIC_URL}/images/login-logo.svg`} alt="Delmont" />
        </div>

        {isSubmitted ? (
          <div className="forgot-success">
            <h2 className="support-title">Thank you!</h2>
            <p>
              Your message has been sent. We&apos;ll get back to you at <strong>{email}</strong> as
              soon as possible.
            </p>
          </div>
        ) : (
          <>
            <h2 className="support-title">How can we help?</h2>

            {error && <div className="login-error">{error}</div>}

            <div className="input-group">
              <label htmlFor="problem" className="input-label">
                Describe the problem you&apos;re having
              </label>
              <input
                id="problem"
                type="text"
                value={problem}
                onChange={(e) => setProblem(e.target.value)}
                placeholder="Delete My Account ...."
                className={`input-field ${fieldErrors.problem ? 'input-error' : ''}`}
              />
              {fieldErrors.problem && <span className="input-error-text">{fieldErrors.problem}</span>}
            </div>

            <div className="input-group">
              <label htmlFor="details" className="input-label">
                Give us the details
              </label>
              <p className="support-hint">
                Give us the details and please be as specific as possible. If it&apos;s a bug, tell us
                the steps we need to take to reproduce the issue, what you expected to happen, and what
                actually happened. We&apos;ll usually reply within a few hours.
              </p>
              <textarea
                id="details"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                className={`support-textarea ${fieldErrors.details ? 'input-error' : ''}`}
                rows={6}
              />
              {fieldErrors.details && <span className="input-error-text">{fieldErrors.details}</span>}
            </div>

            <div className="input-group">
              <label className="input-label">Your contact details</label>
              <div className="support-contact-row">
                <div className="support-contact-field">
                  <input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className={`input-field ${fieldErrors.name ? 'input-error' : ''}`}
                  />
                  {fieldErrors.name && <span className="input-error-text">{fieldErrors.name}</span>}
                </div>
                <div className="support-contact-field">
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value.replace(/\s/g, ''))}
                    onKeyDown={(e) => { if (e.key === ' ') e.preventDefault(); }}
                    placeholder="and your email"
                    className={`input-field ${fieldErrors.email ? 'input-error' : ''}`}
                  />
                  {fieldErrors.email && <span className="input-error-text">{fieldErrors.email}</span>}
                </div>
              </div>
            </div>

            <Button type="submit" variant="primary" className="support-btn" disabled={isLoading}>
              {isLoading ? 'Sending...' : 'Email us'}
            </Button>
          </>
        )}
      </form>
    </div>
  );
}
